import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'wouter';
import { auth as authApi } from '../lib/api.js';
import { takeAuthCallbackToken } from '../lib/auth-callback.js';
import { useAuth } from '../context/AuthContext.js';

export default function AuthCallbackPage() {
  const { setAuth, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const callbackStarted = useRef(false);
  const callbackToken = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (callbackToken.current === undefined) callbackToken.current = takeAuthCallbackToken();
    setTokenLoaded(true);
  }, []);

  const verifyLink = async () => {
    if (isLoading || callbackStarted.current) return;
    callbackStarted.current = true;
    setError(null);
    setVerifying(true);

    const token = callbackToken.current;
    if (!token) {
      setError('This sign-in link is missing or has already been used.');
      setVerifying(false);
      return;
    }
    try {
      const result = await authApi.consumeMagicLink(token);
      callbackToken.current = null;
      flushSync(() => setAuth(result.accessToken, result.user));
      navigate('/', { replace: true });
    } catch (caught) {
      const status =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        typeof caught.status === 'number'
          ? caught.status
          : null;
      const retryable = status === null || status === 429 || status >= 500;
      if (!retryable) callbackToken.current = null;
      callbackStarted.current = false;
      setVerifying(false);
      setError(
        retryable
          ? 'Could not verify this sign-in link. Please try again.'
          : 'This sign-in link is invalid or expired. Request a new link to continue.',
      );
    }
  };

  const hasToken = Boolean(callbackToken.current);
  const displayedError =
    error ??
    (tokenLoaded && !hasToken ? 'This sign-in link is missing or has already been used.' : null);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md text-center" style={{ color: 'var(--ac-text)' }}>
        <h1 className="text-xl font-semibold mb-3">
          {error && hasToken
            ? 'Could not sign in'
            : displayedError
              ? 'Sign-in link unavailable'
              : verifying
                ? 'Signing you in'
                : tokenLoaded
                  ? 'Confirm sign in'
                  : 'Preparing sign in'}
        </h1>
        <p
          role={displayedError ? 'alert' : 'status'}
          aria-live={displayedError ? 'assertive' : 'polite'}
          aria-atomic="true"
          style={{ color: 'var(--ac-text-muted)' }}
        >
          {displayedError ??
            (verifying
              ? 'Verifying your secure link…'
              : tokenLoaded
                ? 'Continue to securely sign in to AetherCasa.'
                : 'Preparing your secure link…')}
        </p>
        {tokenLoaded && hasToken ? (
          <button
            type="button"
            className="mt-5 border border-current px-5 py-3 font-medium disabled:opacity-50"
            disabled={isLoading || verifying}
            onClick={() => void verifyLink()}
          >
            {verifying ? 'Verifying…' : error ? 'Try again' : 'Continue sign in'}
          </button>
        ) : tokenLoaded ? (
          <a href="/login" className="inline-block mt-5 underline">
            Return to sign in
          </a>
        ) : null}
      </div>
    </div>
  );
}
